import {
  Box,
  Button,
  ButtonGroup,
  SnackbarCloseReason,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import {
  a11yProps,
  getStatusUpdatesFromResponse,
  getUserManagementColumns,
  MANAGE_USER_TABLE_WIDTH,
  memberStatKey,
  MemberStats,
  RowData,
  statusMatchesTab,
  UserStatusUpdate,
} from '../../../utils/userManagementUtils';
import {AurDataGrid} from '../../common/aur-data-grid/AurDataGrid';
import {useNavigate} from 'react-router-dom';
import {ReactComponent as PlusIcon} from '../../../assests/svg/plus-icon.svg';
import {
  getTeamMembers,
  resendInvite,
  updateUserStatusAPI,
} from '../../../api/UserManagementService';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import AurMenu, {AurMenuItem} from '../../common/aur-menu/AurMenu';
import AurServerSelect from '../../common/aur-select-dropdown/AurServerSelect';
import {useDispatch, useSelector} from 'react-redux';
import {
  setAnalyticsInfo,
  setProjectList,
  setSelectedUserEmailAddress,
  setTeamMember,
  setClientInfo,
} from '../../../redux/slices/teamMemberSlice';
import AurToast from '../../common/aur-toast/AurToast';
import {RootState} from '../../../redux/store';
import {setShowToastIn} from '../../../redux/slices/bulkUserEditSlice';
import {GridSortModel} from '@mui/x-data-grid';
import {getRolesList} from '../../../api/ClientManagementService';
import {setRoles} from '../../../redux/slices/roleListSlice';
import {
  setFileProcessError,
  setSelectedClient,
} from '../../../redux/slices/bulkUserFileNameSlice';
import AddTeamMembers from '../AddTeamMembers';
import {setmanageTeamMembersPageState} from '../../../redux/slices/pageViewStateSlice';
import AurPopup from '../../common/aur-popup/AurPopup';
import {setShowModal} from '../../../redux/slices/unSaveChangesModalSlice';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {ISelectItem} from '../../common/aur-select-dropdown/types';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {
  CUSTOM_REPORT_COL_HEIGHT,
  failedToLoadStatusCodes,
  INVOICE_REPORT_GRID_ERROR_COMPONENT,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
  TABLE_PAGE_SIZE_OPTION,
  MIN_SEARCH_CHAR,
} from '../../../constants/constants';
import AurButton from '../../common/aur-button/AurButton';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {TeamMemberStatus} from './models';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {ManageTeamMemberRequestPayload} from '../../../types/types';
import {isEmptyInstance} from '../../../utils/utils';

/*
Component for displaying the data of user home page 
*/
const UserManagement = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const [value, setValue] = useState(-1); // default set by useEffect
  const [search, setSearch] = useState<null | string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [rows, setRows] = useState<RowData[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]); // Sorting state
  const [memberStats, setMemberStats] = useState<MemberStats>({
    total: 0,
    active: 0,
    inactive: 0,
    invited: 0,
  });
  const showToastMsg = useSelector(
    (state: RootState) => state.bulkUserEdit.showToastIn
  );
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  /*
Handling active/inactive
*/
  const {showToast} = useToast();
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [hasActive, setHasActive] = useState<boolean>(false);
  const [hasInvited, setHasInvited] = useState<boolean>(false);
  const [hasInactive, setHasInactive] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [showInactiveModal, setShowInactiveModal] = useState<boolean>(false);
  const [isGetRolesListLoading, setIsGetRolesListLoading] = useState(false);
  const [isGetUsersLoading, setIsGetUsersLoading] = useState(false);

  const [clientItems, setClientItems] = useState<ISelectItem[]>([]);
  const [selectedClientItems, setSelectedClientItems] = useState<ISelectItem[]>(
    []
  );

  const [clientPermGroupItems, setClientPermGroupItems] = useState<
    ISelectItem[]
  >([]);

  const [projectPermGroupItems, setProjectPermGroupItems] = useState<
    ISelectItem[]
  >([]);

  const [selectedClientPermGroupItems, setSelectedClientPermGroupItems] =
    useState<ISelectItem[]>([]);

  const [selectedProjectPermGroupItems, setSelectedProjectPermGroupItems] =
    useState<ISelectItem[]>([]);

  const [projectItems, setProjectItems] = useState<ISelectItem[]>([]);
  const [selectedProjectItems, setSelectedProjectItems] = useState<
    ISelectItem[]
  >([]);

  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);

  const isFetchUserInitial = useRef<boolean>(true);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(
    (tableContainerRef.current?.offsetHeight ?? 500) +
      INVOICE_REPORT_GRID_ERROR_COMPONENT
  );
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg')); // Width > 1200px

  const manageTeamMembersViewMode = useSelector(
    (state: RootState) => state.pageViewState.manageTeamMembers
  );

  const loggedInUserId = useSelector(
    (state: RootState) => state.user.user?.userId
  );

  const resetForm = useCallback(() => {
    dispatch(
      setTeamMember({
        firstName: '',
        lastName: '',
        email: '',
        isActive: false,
        isBillable: false,
        isLimitIp: false,
        ipAddressList: [],
        status: TeamMemberStatus.invited,
      })
    );
    dispatch(
      setClientInfo({
        role: '',
        client: '',
        permissionGroup: '',
        permissionGroupId: '',
        project: '',
        workspaces: [],
      })
    );
    dispatch(setProjectList([]));
  }, [dispatch]);

  /**
   * Handle click for row
   * @param data
   */
  const handleRowClick = useCallback(
    (data: any) => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.users.userDetail'),
          type: GA_CLICK_TYPES.LIST,
          region: GA_REGIONS.BODY,
          action: t('gaTracking.users.userDetailOpened'),
        },
      });
      dispatch(
        setAnalyticsInfo({
          userRole: data?.roleName || '',
          numberOfProject: data?.projectCount || '',
        })
      );
      resetForm();
      dispatch(setSelectedUserEmailAddress(data.email));
      navigate('/users/details');
    },
    [dispatch, navigate, resetForm]
  );

  /* Getting user roles */
  const fetchRoles = useCallback(async () => {
    setIsGetRolesListLoading(true);
    try {
      const result = await getRolesList();
      dispatch(setRoles(result.data.data.value));
    } catch (e) {
      console.error('Error', e);
    } finally {
      setIsGetRolesListLoading(false);
    }
  }, [dispatch]);

  const searchFromItemArray = useCallback(
    (searchTerm: string, itemArray: ISelectItem[]) =>
      itemArray.filter((item: ISelectItem) =>
        (item.name ?? '')
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ),
    []
  );

  const searchFromClients = useCallback(
    async (searchTerm: string) => searchFromItemArray(searchTerm, clientItems),
    [clientItems, searchFromItemArray]
  );

  const searchFromClientPermGroups = useCallback(
    async (searchTerm: string) =>
      searchFromItemArray(searchTerm, clientPermGroupItems),
    [clientPermGroupItems, searchFromItemArray]
  );

  const searchFromProjectPermGroups = useCallback(
    async (searchTerm: string) =>
      searchFromItemArray(searchTerm, projectPermGroupItems),
    [projectPermGroupItems, searchFromItemArray]
  );

  const searchFromProjects = useCallback(
    async (searchTerm: string) => searchFromItemArray(searchTerm, projectItems),
    [projectItems, searchFromItemArray]
  );

  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setSize(
          tableContainerRef.current.offsetHeight - CUSTOM_REPORT_COL_HEIGHT
        );
      }
    };
    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => window.removeEventListener('resize', updateHeight);
  });

  useEffect(() => {
    !isFetchUserInitial.current && fetchUsers(currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value,
    currentPage,
    itemsPerPage,
    sortModel,
    search,
    selectedClientItems,
    selectedClientPermGroupItems,
    selectedProjectPermGroupItems,
    selectedProjectItems,
    manageTeamMembersViewMode,
  ]);

  useEffect(() => {
    localStorage.setItem('currentPath', '');
    dispatch(setShowModal(false));
    dispatch(setSelectedClient(''));

    let tabValue = 0;
    switch (manageTeamMembersViewMode) {
      case 'all':
        tabValue = 0;
        break;
      case 'invited':
        tabValue = 1;
        break;
      case 'active':
        tabValue = 2;
        break;
      case 'inactive':
        tabValue = 3;
        break;
    }
    setValue(tabValue);

    if (showToastMsg) {
      setOpenToast(true);
      setTimeout(() => {
        setOpenToast(true);
      }, 5000);

      setInterval(() => {
        setOpenToast(false);
        dispatch(setShowToastIn(false));
      }, 5000);
    }

    fetchRoles();
    fetchInitialUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selectedUsers = rows.filter((user) =>
      selectedIds.includes(user.userClientId)
    );
    const isOnlyLoggedInUserSelected =
      selectedUsers.length === 1 && selectedUsers[0].userId === loggedInUserId;

    const hasActiveUsers = selectedUsers.some((user) =>
      [TeamMemberStatus.inactive, TeamMemberStatus.invited]
        .map((item) => item.toString())
        .includes(user.status)
    );
    setHasActive(hasActiveUsers && !isOnlyLoggedInUserSelected);

    const hasInactiveUsers = selectedUsers.some((user) =>
      [TeamMemberStatus.active, TeamMemberStatus.invited]
        .map((item) => item.toString())
        .includes(user.status)
    );
    setHasInactive(hasInactiveUsers && !isOnlyLoggedInUserSelected);

    setHasInvited(
      selectedUsers.some((user) => user.status === TeamMemberStatus.invited)
    );
  }, [selectedIds, rows]);

  const fetchUsers = useCallback(
    async (pageNumber: number, pageSize: number) => {
      if (isGetUsersLoading) return;

      setIsGetUsersLoading(true);
      try {
        setIsFailedToLoadContent(false);
        const params: ManageTeamMemberRequestPayload = {};

        // Filter
        params.userStatus = manageTeamMembersViewMode;

        params.fetchMasterFilters = isFetchUserInitial.current;
        !isEmptyInstance(search) && (params.SearchTerm = search);

        (selectedClientItems ?? []).length > 0 &&
          (params.clientIds = selectedClientItems.map((i) => i.id).join(','));

        (selectedProjectItems ?? []).length > 0 &&
          (params.projectIds = selectedProjectItems.map((i) => i.id).join(','));

        (selectedClientPermGroupItems ?? []).length > 0 &&
          (params.clientPermGroupIds = selectedClientPermGroupItems
            .map((i) => i.id)
            .join(','));

        (selectedProjectPermGroupItems ?? []).length > 0 &&
          (params.projectPermGroupIds = selectedProjectPermGroupItems
            .map((i) => i.id)
            .join(','));

        // Sort
        (sortModel ?? []).length > 0 &&
          (() => {
            params.SortColumn = sortModel[0].field;
            params.SortOrder = sortModel[0].sort?.toString();
          })();

        // Page
        !isEmptyInstance(pageNumber) && (params.PageNumber = pageNumber);
        !isEmptyInstance(pageSize) && (params.PageSize = pageSize);

        const result = await getTeamMembers(params);
        setRows(result?.data?.data?.users ?? []);

        const totalRowsCount = result?.data?.meta?.pagination?.totalItems ?? 0;
        setTotalRowCount(totalRowsCount);

        const filterData = result?.data?.data?.filterData;
        (filterData?.clients ?? []).length > 0 &&
          setClientItems(filterData?.clients ?? []);

        (filterData?.projects ?? []).length > 0 &&
          setProjectItems(filterData?.projects ?? []);

        (filterData?.clientPermGroups ?? []).length > 0 &&
          setClientPermGroupItems(filterData?.clientPermGroups ?? []);

        (filterData?.projectPermGroups ?? []).length > 0 &&
          setProjectPermGroupItems(filterData?.projectPermGroups ?? []);

        const counts = result?.data?.data?.countData;

        setMemberStats({
          total: counts?.all ?? 0,
          active: counts?.active ?? 0,
          inactive: counts?.inactive ?? 0,
          invited: counts?.invited ?? 0,
        });
      } catch (e: any) {
        failedToLoadStatusCodes.includes(e?.status) &&
          setIsFailedToLoadContent(true);
      } finally {
        setIsGetUsersLoading(false);
      }
    },
    [
      isGetUsersLoading,
      manageTeamMembersViewMode,
      search,
      selectedClientItems,
      selectedClientPermGroupItems,
      selectedProjectItems,
      selectedProjectPermGroupItems,
      sortModel,
    ]
  );

  const fetchInitialUsers = useCallback(async () => {
    try {
      if (memberStats.total > TABLE_PER_PAGE_DEFAULT_50_ROW) {
        setItemsPerPage(TABLE_PER_PAGE_DEFAULT_50_ROW);
        fetchUsers(1, TABLE_PER_PAGE_DEFAULT_50_ROW);
      } else {
        fetchUsers(1, itemsPerPage);
      }
    } finally {
      isFetchUserInitial.current = false;
    }
  }, [fetchUsers, itemsPerPage, memberStats.total]);

  /*
Handler for updating the current value based on a new tab selection
*/
  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setCurrentPage(1);
      switch (newValue) {
        case 0:
          dispatch(setmanageTeamMembersPageState('all'));
          break;
        case 1:
          dispatch(setmanageTeamMembersPageState('invited'));
          break;
        case 2:
          dispatch(setmanageTeamMembersPageState('active'));
          break;
        case 3:
          dispatch(setmanageTeamMembersPageState('inactive'));
          break;
        default:
          dispatch(setmanageTeamMembersPageState('all'));
          break;
      }
      setValue(newValue);
    },
    [dispatch]
  );

  /*
Handler for setting the anchor element when a button is clicked
*/
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  /*
Handler for closing a menu or popover
*/
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const navigateHandler = useCallback(() => {
    resetForm();
    navigate('/users/bulkupload/add');
    dispatch(setFileProcessError(false));
    dispatch(setmanageTeamMembersPageState('all'));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.addGroupOfTeamMemberButton'),
        type: GA_CLICK_TYPES.LINK,
        region: GA_REGIONS.BODY,
        action: location.host + '/users/bulkupload/add',
      },
    });
  }, [dispatch, navigate, resetForm, trackGAEvent]);

  const applyUserStatusUpdates = useCallback(
    (updates: UserStatusUpdate[], previousRows: RowData[]) => {
      if (updates.length === 0) return;

      const updateMap = new Map(updates.map((u) => [u.userId, u.status]));

      setRows((prev) => {
        let next = prev.map((row) => {
          const newStatus = updateMap.get(row.userId);
          return newStatus ? {...row, status: newStatus} : row;
        });
        if (manageTeamMembersViewMode !== 'all') {
          next = next.filter((row) =>
            statusMatchesTab(row.status, manageTeamMembersViewMode)
          );
        }
        return next;
      });

      if (manageTeamMembersViewMode !== 'all') {
        const removedFromView = previousRows.filter(
          (row) =>
            !isEmptyInstance(row?.userId) &&
            updateMap.has(row?.userId) &&
            !statusMatchesTab(
              updateMap.get(row?.userId) ?? '',
              manageTeamMembersViewMode
            )
        ).length;
        if (removedFromView > 0) {
          setTotalRowCount((count) => Math.max(0, count - removedFromView));
        }
      }

      setMemberStats((prev: MemberStats) => {
        const next = {...prev};
        const adjustedUserIds = new Set<string>();

        updates.forEach((update) => {
          if (adjustedUserIds.has(update.userId)) return;

          const oldRow = previousRows.find(
            (r: UserStatusUpdate) => r.userId === update.userId
          );
          if (!oldRow) return;

          adjustedUserIds.add(update.userId);
          const fromKey = memberStatKey(oldRow.status);
          const toKey = memberStatKey(update.status);
          if (fromKey && toKey && fromKey !== toKey) {
            next[fromKey] = Math.max(0, next[fromKey] - 1);
            next[toKey] = next[toKey] + 1;
          }
        });
        return next;
      });
    },
    [manageTeamMembersViewMode]
  );

  // Handle active button click logic

  const handleActiveButtonClick = useCallback(async () => {
    setIsUpdatingStatus(true);
    const selectedRows = rows.filter(
      (row) =>
        selectedIds.includes(row.userClientId) &&
        [TeamMemberStatus.inactive, TeamMemberStatus.invited]
          .map((item) => item.toString())
          .includes(row.status)
    );

    const idsToSend = selectedRows
      .filter((user) => user.userId !== loggedInUserId)
      .map((user) => user.userId);

    const rowsSnapshot = rows;
    await updateUserStatusAPI({
      status: 'Active',
      userIds: [...new Set(idsToSend)],
    })
      .then(async (response) => {
        applyUserStatusUpdates(
          getStatusUpdatesFromResponse(response),
          rowsSnapshot
        );
        await fetchUsers(currentPage, itemsPerPage);
        showToast(
          t('userHomePage.activeSuccess'),
          'success',
          undefined,
          'center'
        );
      })
      .catch((err) => {
        console.error('Error:', err);
      });

    setSelectedIds([]);
    setIsUpdatingStatus(false);
  }, [applyUserStatusUpdates, rows, selectedIds, showToast]);

  const handleInactiveClick = useCallback(() => setShowInactiveModal(true), []);

  const handleInactiveCloseModal = useCallback(() => {
    setShowInactiveModal(false);
    setSelectedIds([]);
  }, []);

  // Handle inactive button click logic
  const handleInactiveButtonClick = useCallback(async () => {
    setShowInactiveModal(false);
    setIsUpdatingStatus(true);
    const selectedRows = rows.filter(
      (row) =>
        selectedIds.includes(row.userClientId) &&
        [TeamMemberStatus.active, TeamMemberStatus.invited]
          .map((item) => item.toString())
          .includes(row.status)
    );

    const idsToSend = selectedRows
      .filter((user) => user.userId !== loggedInUserId)
      .map((user) => user.userId);
    const rowsSnapshot = rows;
    await updateUserStatusAPI({
      status: 'Inactive',
      userIds: [...new Set(idsToSend)],
    })
      .then(async (response) => {
        applyUserStatusUpdates(
          getStatusUpdatesFromResponse(response),
          rowsSnapshot
        );
        await fetchUsers(currentPage, itemsPerPage);
        showToast(
          t('userHomePage.inActiveSuccess'),
          'success',
          undefined,
          'center'
        );
      })
      .catch((err) => {
        console.error('Error:', err);
        showToast(
          t('userHomePage.inActiveError'),
          'error',
          undefined,
          'center'
        );
      });

    setSelectedIds([]);
    setIsUpdatingStatus(false);
  }, [applyUserStatusUpdates, rows, selectedIds, showToast]);

  const menuItems: AurMenuItem[] = useMemo(
    () => [
      {
        label: t('buttons.addNewTeamMember'),
        onClick: () => {
          resetForm();
          dispatch(setmanageTeamMembersPageState('all'));
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.users.addTeamMemberButton'),
              type: GA_CLICK_TYPES.LINK,
              region: GA_REGIONS.BODY,
              action: location.host + '/users/add',
            },
          });
          navigate('/users/add');
        },
      },
      {
        label: t('userHomePage.grpTeamMembers'),
        onClick: navigateHandler,
      },
    ],
    [navigate, navigateHandler, resetForm, trackGAEvent]
  );

  const handleSnackbarClose = useCallback(
    (_: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason === 'clickaway') {
        return;
      }
      setOpenToast(false);
    },
    []
  );

  // Resend Invite Api to send api
  const handleResendInviteClick = useCallback(async () => {
    const selectedRows = rows.filter(
      (row) =>
        selectedIds.includes(row.userClientId) &&
        row.status === TeamMemberStatus.invited
    );

    const userIds: string[] = [
      ...new Set(selectedRows.map((user) => user.userId)),
    ];

    if (userIds.length > 0) {
      try {
        await resendInvite(userIds).then((response) => {
          showToast(
            t('userHomePage.resendInviteSuccess') +
              `${response?.data?.data?.resendInviteCount} ` +
              ` ${response?.data?.data?.resendInviteCount > 1 ? t('userHomePage.users') : t('userHomePage.user')}`,
            'success',
            5000,
            'center'
          );
        });
      } catch (error) {
        console.error('Error resending invites:', error);
      }
    }
  }, [rows, selectedIds, showToast]);

  const noRowsOverlayWhenFailedLoadContent = useMemo(
    () => (isFailedToLoadContent ? AurDataGridErrorOverlay : undefined),
    [isFailedToLoadContent]
  );

  const userListColumns = useMemo(
    () => [
      ...getUserManagementColumns(),
      {
        field: 'arrow',
        headerName: '',
        minWidth: 48,
        flex: 48 / MANAGE_USER_TABLE_WIDTH,
        sortable: false,
        headerClassName: 'no-separator',
        isBorderNotRequired: true,
        renderCell: (params: {row: any}) => (
          <div className={'cell-button'}>
            <AurButton
              type={'text'}
              size={'Medium'}
              startIcon={<AurIcon icon={IconNames.ChevronRight} />}
              ariaLabel={t('userHomePage.viewUserDetail', {
                firstName: `${params?.row?.firstName}`,
              })}
              onClick={() => handleRowClick(params?.row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRowClick(params?.row);
                }
              }}
            />
          </div>
        ),
      },
    ],
    [handleRowClick]
  );

  return (
    <div>
      <div
        className={'user-homepage-header'}
        data-testid={'user-management-header'}
      >
        <Typography
          component="h1"
          variant="h1"
          className={'typo-text headline1_size team-member-style'}
        >
          {t('userHomePage.manageTeamMember')}
        </Typography>
        <Button
          id={'basic-button'}
          aria-controls={open ? 'dropdown-menu' : undefined}
          aria-haspopup={'true'}
          aria-expanded={open ? 'true' : undefined}
          aria-describedby={'addUsersHint'}
          onClick={handleClick}
          startIcon={<PlusIcon aria-hidden />}
          className={
            'typo-text buttonMedium_size custom-menu-button add-members-button custom-button'
          }
          variant={'contained'}
          sx={{
            textTransform: 'none',
          }}
        >
          {t('userHomePage.addTeamMember')}
          <span id={'addUsersHint'} className={'addUserHint'}>
            {t('userHomePage.selectHowAddUsers')}
          </span>
        </Button>

        <AurMenu
          anchorElement={anchorEl}
          shouldShow={open}
          onClose={handleClose}
          menuItems={menuItems}
          direction={'right'}
          aria-labelledby={'addUserButton'}
        />
      </div>
      <Box className={'user-homepage-content'}>
        <Box className={'dropdowns'}>
          <AurSearchBox
            onSearch={(value) => {
              setCurrentPage(1);
              setSearch(value);
            }}
            minCharForSearch={MIN_SEARCH_CHAR}
            classNameProps={'custom-search'}
          />
          <Box className={'server-select-container'}>
            <AurServerSelect
              isLoading={isGetUsersLoading}
              items={clientItems}
              itemsPerPage={clientItems.length}
              onSearch={searchFromClients}
              hasNextPage={false}
              searchPlaceHolder={t('userHomePage.search')}
              itemsCountwithNoScroll={3}
              itemDisplay={'name'}
              itemKey={'id'}
              dropdownLabel={t('userHomePage.client')}
              onSelectionChange={(data) => {
                setCurrentPage(1);
                setSelectedClientItems(data ?? []);
              }}
              onClearClicked={() => setSelectedClientItems([])}
              hasValidationError={false}
              validationText={''}
              helperText={''}
              aria-label={t('userHomePage.client')}
              labelId={'client'}
              defaultSelection={selectedClientItems}
              showCheckIcon={false}
              maxDropdownItemLength={isLargeScreen ? 50 : 40}
            />
            <AurServerSelect
              isLoading={isGetUsersLoading}
              items={clientPermGroupItems}
              itemsPerPage={clientPermGroupItems.length}
              onSearch={searchFromClientPermGroups}
              hasNextPage={false}
              searchPlaceHolder={t('userHomePage.search')}
              itemsCountwithNoScroll={3}
              itemDisplay={'name'}
              itemKey={'id'}
              dropdownLabel={t('userHomePage.clientPermGroup')}
              onSelectionChange={(data) => {
                setCurrentPage(1);
                setSelectedClientPermGroupItems(data ?? []);
              }}
              onClearClicked={() => setSelectedClientPermGroupItems([])}
              hasValidationError={false}
              validationText={''}
              helperText={''}
              aria-label={t('userHomePage.clientPermGroup')}
              labelId={'client-perm-group'}
              defaultSelection={selectedClientPermGroupItems}
              showCheckIcon={false}
              maxDropdownItemLength={isLargeScreen ? 50 : 40}
            />
            <AurServerSelect
              isLoading={isGetUsersLoading}
              items={projectPermGroupItems}
              itemsPerPage={projectPermGroupItems.length}
              onSearch={searchFromProjectPermGroups}
              hasNextPage={false}
              searchPlaceHolder={t('userHomePage.search')}
              itemsCountwithNoScroll={3}
              itemDisplay={'name'}
              itemKey={'id'}
              dropdownLabel={t('userHomePage.projectPermGroup')}
              onSelectionChange={(data) => {
                setCurrentPage(1);
                setSelectedProjectPermGroupItems(data ?? []);
              }}
              onClearClicked={() => setSelectedProjectPermGroupItems([])}
              hasValidationError={false}
              validationText={''}
              helperText={''}
              aria-label={t('userHomePage.projectPermGroup')}
              labelId={'project-perm-group'}
              defaultSelection={selectedProjectPermGroupItems}
              showCheckIcon={false}
              maxDropdownItemLength={isLargeScreen ? 50 : 40}
            />
            <AurServerSelect
              isLoading={isGetUsersLoading}
              items={projectItems}
              itemsPerPage={projectItems.length}
              onSearch={searchFromProjects}
              hasNextPage={false}
              searchPlaceHolder={t('userHomePage.search')}
              itemsCountwithNoScroll={3}
              itemDisplay={'name'}
              itemKey={'id'}
              dropdownLabel={t('userHomePage.prjGroup')}
              onSelectionChange={(data) => {
                setCurrentPage(1);
                setSelectedProjectItems(data ?? []);
              }}
              onClearClicked={() => setSelectedProjectItems([])}
              hasValidationError={false}
              validationText={''}
              helperText={''}
              aria-label={t('userHomePage.prjGroup')}
              labelId={'project-group'}
              defaultSelection={selectedProjectItems}
              showCheckIcon={false}
              maxDropdownItemLength={isLargeScreen ? 50 : 40}
            />
          </Box>
        </Box>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <Tabs
            data-testid={'Tabs'}
            value={value}
            onChange={handleChange}
            aria-label={t('userHomePage.memberTypeTab')}
            className={'aur-tabs user-page-tabs'}
          >
            <Tab
              data-testid={'all'}
              label={t('userHomePage.allTeamMem') + ` (${memberStats.total})`}
              {...a11yProps(0)}
              tabIndex={0}
            />
            <Tab
              data-testid={'invited'}
              label={t('userHomePage.invited') + ` (${memberStats.invited})`}
              {...a11yProps(1)}
              tabIndex={0}
            />
            <Tab
              data-testid={'active'}
              label={t('userHomePage.active') + ` (${memberStats.active})`}
              {...a11yProps(2)}
              tabIndex={0}
            />
            <Tab
              data-testid={'inactive'}
              label={t('userHomePage.inactive') + ` (${memberStats.inactive})`}
              {...a11yProps(3)}
              tabIndex={0}
            />
          </Tabs>
        </Box>
        <Box
          className={'button-grp-box'}
          role="group"
          aria-label={t('userHomePage.bulkAction')}
        >
          <ButtonGroup
            variant={'contained'}
            aria-label={t('userHomePage.bulkActionGroup')}
            className={'button-grp'}
          >
            <Button
              disabled={!hasActive}
              data-testid={'mark-active'}
              className={'inner-btn typo-text buttonSmall_size'}
              onClick={handleActiveButtonClick}
              aria-label={t('userHomePage.markActive')}
            >
              {t('userHomePage.markActive')}
            </Button>
            <Button
              disabled={!hasInactive}
              data-testid={'mark-inactive'}
              className={'inner-btn typo-text buttonSmall_size'}
              onClick={handleInactiveClick}
              aria-label={t('userHomePage.markInactive')}
            >
              {t('userHomePage.markInactive')}
            </Button>
            <Button
              disabled={!hasInvited}
              className={'inner-btn typo-text buttonSmall_size'}
              onClick={handleResendInviteClick}
              data-testid={'resend-invites'}
              aria-label={t('userHomePage.resentInvite')}
            >
              {t('userHomePage.resentInvite')}
            </Button>
          </ButtonGroup>
        </Box>
        <Box className={'user-management-grid-container'}>
          <AurDataGrid
            className={'user-management-grid'}
            rows={rows}
            columns={userListColumns}
            visibleToolTip={true}
            pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
            checkBoxVisible={true}
            entityType={t('common.teamMembers')}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            getRowId={(row) => row.userClientId}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalRowCount={totalRowCount}
            sortModel={sortModel}
            setSortModel={setSortModel}
            disableVirtualization
            handleRowClick={(params: {row: any}) => handleRowClick(params?.row)}
            onRefresh={() => fetchUsers(currentPage, itemsPerPage)}
            containerHeight={size}
            noRowsOverlay={
              rows.length === 0
                ? AddTeamMembers
                : noRowsOverlayWhenFailedLoadContent
            }
          />
        </Box>
        <Box
          className={'toast-banner-container'}
          role={'alert'}
          data-testid={'user-management-toast'}
        >
          <AurToast
            ariaLabel={t('common.toastAlert.toastAlertHint')}
            hideDuration={5000}
            open={openToast}
            message={t('common.toastAlert.message')}
            onClose={() => handleSnackbarClose}
          />
        </Box>
      </Box>
      <AurPopup
        aria-label={t('userHomePage.inActiveHeading')}
        className={'inactivity-popup-modal'}
        open={showInactiveModal}
        onOk={handleInactiveButtonClick}
        onCancel={handleInactiveCloseModal}
        popupHeading={t('userHomePage.inActiveHeading')}
        showCloseIcon={false}
        showPopupTitle={true}
        showPopupHeading={true}
        popupTitle={t('common.unSavedChangesPopup.title')}
        labelActionCancel={t('common.unSavedChangesPopup.cancelBtn')}
        labelActionOk={t('userHomePage.inActiveOk')}
      />
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={isGetRolesListLoading || isGetUsersLoading || isUpdatingStatus}
        />
      }
    </div>
  );
};

export default UserManagement;
